import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Button,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Notifications,
  Message,
  Settings,
  Logout,
  DarkMode,
  LightMode,
  Menu as MenuIcon,
  RssFeed,
  Movie,
  Tv,
  Person,
  Bookmark,
  EmojiEvents,
  Help,
  Feedback,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { useChat } from '../../contexts/ChatContext';
import S3Avatar from '../Common/S3Avatar';
import BadgedAvatar from '../Common/BadgedAvatar';
import EnhancedNotificationBell from '../Notifications/EnhancedNotificationBell';
import { showAuthAlert } from '../../utils/authAlerts';
import { navigateToSignIn } from '../../utils/navigationHelpers';
import { HypeCircleLogo } from '../Branding';
import { achievementService } from '../../services/achievementService';

const Navbar = ({ onSidebarToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, signOut } = useAuth();
  const { toggleTheme, themeMode, addNotification } = useApp();
  const { unreadCount } = useChat();

  // Play sound when unread count increases
  const prevUnreadRef = React.useRef(0);
  const audioRef = React.useRef(null);
  
  React.useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/sounds/message.mp3');
      audioRef.current.volume = 0.5;
    }
  }, []);
  
  React.useEffect(() => {
    if (user && unreadCount !== undefined && unreadCount > prevUnreadRef.current && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => console.log('Sound failed:', err));
    }
    prevUnreadRef.current = unreadCount;
  }, [user, unreadCount]);

  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [userBadges, setUserBadges] = useState([]);

  // Load user badges with debouncing and caching
  React.useEffect(() => {
    if (!user?.id) {
      setUserBadges([]);
      return;
    }

    // Use cached badges if available
    const cacheKey = `badges-${user.id}-${user.updatedAt || Date.now()}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setUserBadges(JSON.parse(cached));
        return;
      } catch (e) {
        // Invalid cache, continue to fetch
      }
    }

    // Debounce badge loading
    const timeoutId = setTimeout(() => {
      achievementService.getUserBadges(user.id)
        .then(badges => {
          setUserBadges(badges);
          // Cache for 5 minutes
          sessionStorage.setItem(cacheKey, JSON.stringify(badges));
          setTimeout(() => sessionStorage.removeItem(cacheKey), 5 * 60 * 1000);
        })
        .catch(error => {
          console.error('Navbar: Failed to load user badges:', error);
          setUserBadges([]);
        });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [user?.id]);


  const handleSearch = (e) => {
    e.preventDefault();
    if (!user) {
      showAuthAlert(addNotification, navigate, 'SEARCH');
      return;
    }
    if (searchQuery.trim()) {
      navigate(`/search/${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };



  const handleSignOut = async () => {
    handleUserMenuClose();
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Sign out error:', error);
      navigate('/auth');
    }
  };

  const handleSignIn = () => {
    try {
      navigateToSignIn(navigate, { returnUrl: location.pathname });
    } catch (error) {
      console.error('Sign-in navigation failed:', error);
      // Fallback to direct navigation
      window.location.href = '/auth';
    }
    handleUserMenuClose();
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleUserMenuClose();
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    handleUserMenuClose();
  };

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        minHeight: { xs: 56, sm: 64 },
        px: { xs: 1.5, sm: 3 },
        maxWidth: '100vw',
        overflow: 'hidden'
      }}>
        {/* Left side: Logo and navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton edge="start" color="inherit" onClick={onSidebarToggle} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6"
            sx={{ 
              cursor: 'pointer', 
              mr: 3,
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              color: 'primary.main'
            }}
            onClick={() => navigate('/')}
          >
            HypeCircle
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Browse Movies" arrow>
                <Button
                  color="inherit"
                  onClick={() => navigate('/movies')}
                  startIcon={<Movie />}
                  sx={{
                    px: location.pathname.startsWith('/movies') ? 2 : 1,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: location.pathname.startsWith('/movies') ? 600 : 400,
                    fontSize: '1rem',
                    bgcolor: location.pathname.startsWith('/movies') ? 'primary.main' : 'transparent',
                    color: location.pathname.startsWith('/movies') ? 'white' : 'inherit',
                    minWidth: location.pathname.startsWith('/movies') ? 'auto' : 48,
                    '&:hover': {
                      bgcolor: location.pathname.startsWith('/movies') ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  {location.pathname.startsWith('/movies') ? 'Movies' : ''}
                </Button>
              </Tooltip>
              <Tooltip title="Browse TV Shows" arrow>
                <Button
                  color="inherit"
                  onClick={() => navigate('/shows')}
                  startIcon={<Tv />}
                  sx={{
                    px: location.pathname.startsWith('/shows') ? 2 : 1,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: location.pathname.startsWith('/shows') ? 600 : 400,
                    fontSize: '1rem',
                    bgcolor: location.pathname.startsWith('/shows') ? 'primary.main' : 'transparent',
                    color: location.pathname.startsWith('/shows') ? 'white' : 'inherit',
                    minWidth: location.pathname.startsWith('/shows') ? 'auto' : 48,
                    '&:hover': {
                      bgcolor: location.pathname.startsWith('/shows') ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  {location.pathname.startsWith('/shows') ? 'Shows' : ''}
                </Button>
              </Tooltip>
              {user && (
                <Tooltip title="Social Feed - See posts from users you follow" arrow>
                  <Button
                    color="inherit"
                    onClick={() => navigate('/feed')}
                    startIcon={<RssFeed />}
                    sx={{
                      px: location.pathname.startsWith('/feed') ? 2 : 1,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: location.pathname.startsWith('/feed') ? 600 : 400,
                      fontSize: '0.95rem',
                      bgcolor: location.pathname.startsWith('/feed') ? 'primary.main' : 'transparent',
                      color: location.pathname.startsWith('/feed') ? 'white' : 'inherit',
                      minWidth: location.pathname.startsWith('/feed') ? 'auto' : 40,
                      '&:hover': {
                        bgcolor: location.pathname.startsWith('/feed') ? 'primary.dark' : 'action.hover',
                      },
                    }}
                  >
                    {location.pathname.startsWith('/feed') ? 'Feed' : ''}
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Center (optional): could place the search bar here */}
        {!isMobile && (
          <Box component="form" onSubmit={handleSearch} sx={{ flexGrow: 1,  mx: { xs: 1, md: 3 } , maxWidth: 400 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder={user ? "Search users, posts, movies..." : "Sign in to search"}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={!user}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'action.hover',
                },
              }}
            />
          </Box>
        )}

        {/* Right side: Theme toggle, notifications, messages, user avatar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0, pr: { xs: 1, sm: 0 } }}>
          {/* Anonymous user navigation */}
          {!user && (
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
              <Button
                color="inherit"
                onClick={() => navigate('/about')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  px: { xs: 0.75, sm: 1.5 },
                  minWidth: 'auto'
                }}
              >
                About
              </Button>
              {/* <Button
                color="inherit"
                onClick={() => navigate('/support')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
              >
Contact & Support
              </Button> */}
            </Box>
          )}

          {isMobile ? (
            <IconButton onClick={toggleTheme} color="inherit" size="small" sx={{ p: 0.5 }}>
              {themeMode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
          ) : (
            <Tooltip title={`Switch to ${themeMode === 'dark' ? 'Light' : 'Dark'} Mode`} arrow>
              <IconButton onClick={toggleTheme} color="inherit" size="small" sx={{ p: 1 }}>
                {themeMode === 'dark' ? <LightMode /> : <DarkMode />}
              </IconButton>
            </Tooltip>
          )}

          {user && <EnhancedNotificationBell />}

          {/* Chat button for authenticated users only */}
          {!isMobile && user && (
            <Tooltip title={`Messages${unreadCount > 0 ? ` - ${unreadCount} unread` : ''}`} arrow>
              <Button
                color="inherit"
                onClick={() => navigate('/chat')}
                startIcon={
                  <Badge 
                    badgeContent={unreadCount > 0 ? unreadCount : null} 
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.75rem',
                        minWidth: '18px',
                        height: '18px',
                        borderRadius: '9px'
                      }
                    }}
                  >
                    <Message sx={{ fontSize: 22 }} />
                  </Badge>
                }
                sx={{
                  px: location.pathname.startsWith('/chat') ? 2 : 1,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: location.pathname.startsWith('/chat') ? 600 : 400,
                  fontSize: '0.95rem',
                  bgcolor: location.pathname.startsWith('/chat') ? 'primary.main' : 'transparent',
                  color: location.pathname.startsWith('/chat') ? 'white' : 'inherit',
                  minWidth: location.pathname.startsWith('/chat') ? 'auto' : 40,
                  '&:hover': {
                    bgcolor: location.pathname.startsWith('/chat') ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                {location.pathname.startsWith('/chat') ? `Chat${unreadCount > 0 ? ` (${unreadCount})` : ''}` : ''}
              </Button>
            </Tooltip>
          )}

          {user ? (
            <Box>
              <Button
                onClick={handleUserMenuClick}
                sx={{
                  p: 0.5,
                  borderRadius: 3,
                  minWidth: 'auto',
                  color: 'inherit',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 193, 7, 0.08)'
                      : 'rgba(25, 118, 210, 0.08)',
                  }
                }}
                title="Account Menu"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BadgedAvatar 
                    user={user} 
                    badges={userBadges}
                    size={32}
                  />
                  <KeyboardArrowDown 
                    sx={{ 
                      fontSize: 16, 
                      color: 'text.secondary',
                      transform: Boolean(userMenuAnchor) ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease-in-out'
                    }} 
                  />
                </Box>
              </Button>

              {/* User menu dropdown */}
              <Menu 
                anchorEl={userMenuAnchor} 
                open={Boolean(userMenuAnchor)} 
                onClose={handleUserMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 280,
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                      : '0 8px 32px rgba(0, 0, 0, 0.12)',
                    border: `1px solid ${theme.palette.divider}`,
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(30, 30, 30, 0.95)'
                      : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {/* User Info Header */}
                <Box sx={{ 
                  px: 3, 
                  py: 2, 
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(0, 0, 0, 0.1))'
                    : 'linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(255, 255, 255, 0.1))',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  mb: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BadgedAvatar 
                      user={user} 
                      badges={userBadges}
                      size={48}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {user?.displayName}
                      </Typography>
                      {(() => {
                        // Get active title from titleOrder or titles array (same logic as UserProfile)
                        let activeTitle = null;
                        if (user?.titleOrder && user.titleOrder.length > 0) {
                          activeTitle = user.titleOrder[0];
                        } else if (user?.titles && user.titles.length > 0) {
                          activeTitle = user.titles[0];
                        }
                        
                        return activeTitle ? (
                          <Typography 
                            variant="body2" 
                            color="primary.main" 
                            noWrap 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              '&:hover::after': {
                                content: '" (Achievement)"',
                                fontSize: '0.6rem',
                                fontWeight: 400,
                                color: 'text.secondary',
                                textTransform: 'none',
                              }
                            }}
                          >
                            {activeTitle}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontWeight: 500 }}>
                            Movie Enthusiast
                          </Typography>
                        );
                      })()}
                      <Typography variant="caption" color="text.secondary">
                        {user?.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Menu Items */}
                <MenuItem 
                  onClick={handleProfileClick}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 193, 7, 0.08)'
                        : 'rgba(25, 118, 210, 0.08)',
                    }
                  }}
                >
                  <Person sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>My Profile</Typography>
                    <Typography variant="caption" color="text.secondary">View and edit profile</Typography>
                  </Box>
                </MenuItem>

                <MenuItem 
                  onClick={() => {
                    navigate('/my-watchlist');
                    handleUserMenuClose();
                  }}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 193, 7, 0.08)'
                        : 'rgba(25, 118, 210, 0.08)',
                    }
                  }}
                >
                  <Bookmark sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>My Watchlist</Typography>
                    <Typography variant="caption" color="text.secondary">Movies and shows to watch</Typography>
                  </Box>
                </MenuItem>

                <MenuItem 
                  onClick={() => {
                    navigate('/leaderboards');
                    handleUserMenuClose();
                  }}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 193, 7, 0.08)'
                        : 'rgba(25, 118, 210, 0.08)',
                    }
                  }}
                >
                  <EmojiEvents sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Achievements</Typography>
                    <Typography variant="caption" color="text.secondary">View badges and leaderboards</Typography>
                  </Box>
                </MenuItem>

                <Divider sx={{ my: 1 }} />

                <MenuItem 
                  onClick={handleSettingsClick}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 193, 7, 0.08)'
                        : 'rgba(25, 118, 210, 0.08)',
                    }
                  }}
                >
                  <Settings sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Settings</Typography>
                    <Typography variant="caption" color="text.secondary">Account and preferences</Typography>
                  </Box>
                </MenuItem>

                <MenuItem 
                  onClick={() => {
                    navigate('/about');
                    handleUserMenuClose();
                  }}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 193, 7, 0.08)'
                        : 'rgba(25, 118, 210, 0.08)',
                    }
                  }}
                >
                  <Help sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>About Us</Typography>
                    <Typography variant="caption" color="text.secondary">Learn about HypeCircle</Typography>
                  </Box>
                </MenuItem>

                <MenuItem 
                  onClick={() => {
                    navigate('/contact');
                    handleUserMenuClose();
                  }}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 193, 7, 0.08)'
                        : 'rgba(25, 118, 210, 0.08)',
                    }
                  }}
                >
                  <Feedback sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Contact Us</Typography>
                    <Typography variant="caption" color="text.secondary">Get in touch with our team</Typography>
                  </Box>
                </MenuItem>

                <Divider sx={{ my: 1 }} />

                <MenuItem 
                  onClick={handleSignOut}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'error.main',
                      '& .MuiTypography-root': {
                        color: 'white',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      },
                    }
                  }}
                >
                  <Logout sx={{ mr: 2, color: 'error.main' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500} color="error.main">Sign Out</Typography>
                    <Typography variant="caption" color="text.secondary">Sign out of your account</Typography>
                  </Box>
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSignIn}
              size="small"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 2.5 },
                py: { xs: 0.5, sm: 1 },
                minWidth: { xs: 60, sm: 90 },
                whiteSpace: 'nowrap',
                flexShrink: 0,
                mr: { xs: 1, sm: 0 },
                borderRadius: 2,
                fontWeight: 600,
                boxShadow: (theme) => theme.palette.mode === 'dark'
                  ? '0 4px 12px rgba(255, 193, 7, 0.3)'
                  : '0 4px 12px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 6px 16px rgba(255, 193, 7, 0.4)'
                    : '0 6px 16px rgba(25, 118, 210, 0.4)'
                }
              }}
            >
              Sign In
            </Button>
          )}


        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
